import React, { useEffect } from 'react';
import moment from 'moment';
import { Tooltip } from '@material-ui/core';
import PropTypes from 'prop-types';
import { Field } from 'redux-form/immutable';
import { formatInTimeZone } from 'date-fns-tz';
import { convertTokens } from '@date-fns/upgrade/v2';
import FormLabel from '@material-ui/core/FormLabel';
import _ from 'lodash';

import './style.css';

import FormDateSelector from 'retired/FormDateSelector';
import { gray2 } from 'utils/color-styles';
import { TimeZonePicker } from 'components/TimeZone/TimeZonePicker';

const FormDateWithStartAndEndTime = ({
  defaults = [],
  names,
  minDate,
  maxDate,
  isPreviousDateDisabled,
  isNextDateDisabled,
  disabled,
  displayTimeAndTimeZone,
  timeZone,
  setTimeZone,
  timeZoneDisabled,
  allowClear,
  ...props
}) => {
  const [passedDefaultStartingAt, passedDefaultEndingAt] = defaults;
  const [startingAtFieldName, endingAtInputName] = names;
  const startingAtField = _.get(props, startingAtFieldName);
  const endingAtField = _.get(props, endingAtInputName);
  const startingAtMoment = startingAtField.input.value ? moment(startingAtField.input.value) : null;
  const endingAtMoment = endingAtField.input.value ? moment(endingAtField.input.value) : null;
  const minMoment = minDate ? moment(minDate) : null;
  const maxMoment = maxDate ? moment(maxDate) : null;
  const defaultStartingAt = passedDefaultStartingAt ? moment(passedDefaultStartingAt) : null;
  const defaultEndingAt = passedDefaultEndingAt ? moment(passedDefaultEndingAt) : null;

  const duration =
    (startingAtMoment &&
      endingAtMoment &&
      moment.duration(startingAtMoment.diff(endingAtMoment))) ??
    null;

  useEffect(() => {
    if (!startingAtField.input.value && defaultStartingAt) {
      startingAtField.input.onChange(defaultStartingAt);
    }
    if (!endingAtField.input.value && defaultEndingAt) {
      endingAtField.input.onChange(defaultEndingAt);
    }
  }, []);

  const getTimeZone = () =>
    timeZone || (typeof Intl === 'object' && Intl.DateTimeFormat()?.resolvedOptions()?.timeZone);
  const getUTC = () =>
    timeZone
      ? formatInTimeZone(
          startingAtMoment ? startingAtMoment.toDate() : Date.now(),
          timeZone,
          convertTokens('Z')
        )
      : moment().format('Z');

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'flex-start' }}>
        <Field
          name={startingAtFieldName}
          disabled={disabled}
          component={FormDateSelector}
          noDate={isPreviousDateDisabled && isNextDateDisabled}
          placeholder="Date *"
          isPreviousDateDisabled={isPreviousDateDisabled}
          isNextDateDisabled={isNextDateDisabled}
          placeholderTime="Start Time *"
          defaultValue={defaultStartingAt}
          minDate={minMoment ?? moment().startOf('day')}
          maxDate={maxMoment ?? null}
          allowClear={allowClear}
          onChange={(_event, newValue) => {
            if (newValue) {
              const defaultNewEndingAtMoment = newValue.clone().add(2, 'hours');
              let newEndingAtMoment = endingAtMoment ?? defaultNewEndingAtMoment;

              if (Math.abs(newValue.diff(newEndingAtMoment, 'hours', true)) > 24) {
                newEndingAtMoment = newEndingAtMoment.clone().set({
                  year: newValue.get('year'),
                  month: newValue.get('month'),
                  date: newValue.get('date'),
                });
              }

              if (endingAtMoment && newValue.isSameOrAfter(newEndingAtMoment)) {
                newEndingAtMoment = defaultNewEndingAtMoment;
              }

              if (maxMoment && newEndingAtMoment.isAfter(maxMoment)) {
                newEndingAtMoment = maxMoment.clone();
              }

              if (endingAtMoment && newEndingAtMoment.isSame(endingAtMoment)) {
                return;
              }

              endingAtField.input.onChange(newEndingAtMoment);
              return;
            }

            if (defaultStartingAt) {
              setTimeout(() => startingAtField.input.onChange(defaultStartingAt), 0);
              return;
            }

            endingAtField.input.onChange(null);
          }}
        />
        <Field
          disabled={disabled}
          name={endingAtInputName}
          component={FormDateSelector}
          noDate
          placeholder="End Time *"
          defaultValue={defaultEndingAt}
          minDate={startingAtMoment ?? minMoment ?? null}
          maxDate={maxMoment ?? null}
          allowClear={allowClear}
          onChange={(_event, newValue) => {
            if (newValue) {
              const value = startingAtMoment ? startingAtMoment.clone() : moment();
              value.hour(newValue.hour());
              value.minutes(newValue.minutes());
              setTimeout(() => endingAtField.input.onChange(value), 0);
              return;
            }

            if (defaultEndingAt) {
              setTimeout(() => endingAtField.input.onChange(defaultEndingAt), 0);
            }
          }}
        />
        {!!setTimeZone && (
          <TimeZonePicker
            timeZone={timeZone}
            setTimeZone={setTimeZone}
            disabled={timeZoneDisabled}
          />
        )}
        <div>
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              height: 40,
              alignItems: 'center',
            }}
          >
            {displayTimeAndTimeZone && (
              <Tooltip title={getTimeZone()}>
                <div
                  style={{
                    marginTop: 5,
                    fontSize: 11,
                    color: gray2,
                    width: 70,
                  }}
                >
                  UTC{getUTC()}
                </div>
              </Tooltip>
            )}
            {duration && !setTimeZone && (
              <div
                style={{
                  marginTop: 5,
                  marginLeft: 2,
                  fontSize: 11,
                  color: gray2,
                  width: 100,
                }}
              >
                {duration.humanize()}
              </div>
            )}
          </div>
        </div>
      </div>
      {((startingAtField.meta?.error && startingAtField.meta?.touched) ||
        (endingAtField.meta?.error && endingAtField.meta?.touched)) && (
        <FormLabel error style={{ fontSize: 12, marginTop: 10 }}>
          Required
        </FormLabel>
      )}
    </>
  );
};

FormDateWithStartAndEndTime.propTypes = {
  defaults: PropTypes.arrayOf(PropTypes.instanceOf(Date)),
  names: PropTypes.arrayOf(PropTypes.string).isRequired,
  minDate: PropTypes.object,
  maxDate: PropTypes.object,
  isPreviousDateDisabled: PropTypes.bool,
  isNextDateDisabled: PropTypes.bool,
  disabled: PropTypes.bool,
  displayTimeAndTimeZone: PropTypes.bool,
  timeZone: PropTypes.string,
  setTimeZone: PropTypes.func,
  timeZoneDisabled: PropTypes.bool,
  allowClear: PropTypes.bool,
};

export default FormDateWithStartAndEndTime;
